import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextCaption, TextTitle } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInputAmount } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FixedLimitStackParamList, FixedLimitStackRoutes } from '@/features/account-management/screens/fixed-limit/type';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimal } from '../../utils';

export interface FixedLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const FixedLimitWebZeroScreen: React.FC<FixedLimitProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn, flexContainer, summaryContent } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<FixedLimitStackParamList>>();

  const [fixedLimit, setFixedLimit] = useState<any>({
    limit: undefined,
    name: record?.name,
    currency: record?.currency
  });
  const [errorMsg, setErrorMsg] = useState<any>({ limit: undefined });

  const [key, setKey] = useState('');
  useFocusEffect(
    React.useCallback(() => {
      setKey(keyGenerator('withdraw'));
    }, [])
  );

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !fixedLimit.limit) || (keys.includes('limit') && !data?.limit)) {
      errorMsgFlag.limit = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && fixedLimit.limit) || (keys.includes('limit') && data?.limit)) {
      errorMsgFlag.limit = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeFixedLimit = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: fixedLimit });
      navigation.navigate(FixedLimitStackRoutes.Confirm, {
        onLayout,
        data: fixedLimit,
        func,
        onReturnData,
        refresh
      });
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}
      key={key}>
      <ModalHeader title={t('card-account-management::fixed-limit.title--fixed-limit')} closeModal={onClose} />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextTitle sx={textMb} semiBold>
            {t('card-account-management::fixed-limit.form-text--fill-out-form-to-fixed-limit')}
          </TextTitle>
          <TextCaption semiBold label>
            {t('card-account-management::fixed-limit.title-credit-summary')}
          </TextCaption>
          <View sx={flexContainer}>
            <View sx={summaryContent}>
              <TextCaption>{t('card-account-management::fixed-limit.text-funding-account')}</TextCaption>
              <TextCaption semiBold>{record?.creditSummary?.accountName ?? ''}</TextCaption>
            </View>
            <View sx={summaryContent}>
              <TextCaption>{t('card-account-management::fixed-limit.text-total-credit')}</TextCaption>
              <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.totalCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
            </View>
            <View sx={summaryContent}>
              <TextCaption>{t('card-account-management::fixed-limit.text-total-assigned-credit')}</TextCaption>
              <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.totalAssignedCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
            </View>
            <View sx={summaryContent}>
              <TextCaption>{t('card-account-management::fixed-limit.text-available-credit')}</TextCaption>
              <TextCaption semiBold>{formatAmountByDecimal({ value: record?.creditSummary?.availableCredit, suffix: record?.currency, decimals: 2 })}</TextCaption>
            </View>
          </View>
          <View sx={{ marginTop: '$4', gap: '$5' }}>
            <UniInputAmount
              labelText={t('card-account-management::fixed-limit.form-text--limit')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center', flexDirection: 'row-reverse' }}
              asset={fixedLimit.currency}
              errorText={errorMsg.limit}
              value={fixedLimit.limit}
              decimals={2}
              onValueChange={(value) => {
                setFixedLimit({
                  ...fixedLimit,
                  limit: value
                });
                validateForm({ limit: value?.toString() });
              }}
            />
          </View>
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::general.button--cancel')}
              onPress={onClose}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::change-atm-pin.button--update')}
              onPress={changeFixedLimit}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  },
  flexContainer: {
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10,
    padding: 16,
    gap: 4
  },
  summaryContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  }
};
